import { LitElement, html, nothing } from "lit";
import { customElement, property } from "lit/decorators.js";
import { classMap } from "lit/directives/class-map.js";
import { tabBarStyles } from "./tab-bar.css";

export interface TabItem {
  key: string;
  label: string;
  badge?: number;
  disabled?: boolean;
  testId?: string;
}

/**
 * Reusable tab bar component for switching between content panels.
 *
 * @fires tab-change - Dispatched when a tab is clicked. Detail: { key: string }
 *
 * @example
 * <wow-tab-bar
 *   .tabs=${[{ key: "overview", label: "Overview" }, { key: "chat", label: "Event Chat" }]}
 *   active-tab="overview"
 *   @tab-change=${(e) => this.activeTab = e.detail.key}
 * ></wow-tab-bar>
 *
 * @cssProperty --tab-bar-gap - Gap between tabs (default: var(--sds-size-space-800))
 * @cssProperty --tab-bar-active-color - Active tab text color
 * @cssProperty --tab-bar-active-border-color - Active tab underline color
 */
@customElement("wow-tab-bar")
export class WowTabBar extends LitElement {
  static override styles = tabBarStyles;

  @property({ type: Array })
  tabs: TabItem[] = [];

  @property({ type: String, attribute: "active-tab" })
  activeTab = "";

  private handleTabClick(key: string) {
    this.dispatchEvent(
      new CustomEvent("tab-change", {
        detail: { key },
        bubbles: true,
        composed: true,
      }),
    );
  }

  override render() {
    return html`
      <div class="tab-bar" role="tablist">
        ${this.tabs.map(
          (tab) => html`
            <button
              class=${classMap({ "tab-button": true, active: this.activeTab === tab.key })}
              role="tab"
              aria-selected=${this.activeTab === tab.key}
              data-testid=${tab.testId ?? `tab-${tab.key}`}
              ?disabled=${tab.disabled ?? false}
              @click=${() => this.handleTabClick(tab.key)}
            >
              ${tab.label}
              ${tab.badge ? html`<span class="tab-badge">${tab.badge > 99 ? "99+" : tab.badge}</span>` : nothing}
            </button>
          `,
        )}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "wow-tab-bar": WowTabBar;
  }
}
